/**
 * Agent Chat 말풍선 렌더러 — 인라인 마크다운 + ```chart·```tree·```flow 블록 + 폭 맞춤 표.
 *
 * react-markdown 등 라이브러리 의존 없이 자체 변환(components/ai/aiMarkdown.js)을 쓴다.
 * 스트리밍 중에는 아직 닫히지 않은 펜스 블록을 숨긴다 — 안 그러면 raw JSON 이 말풍선에 노출된다.
 *
 * 파이프 표는 HTML <table> 이 아니라 FitTable 로 그린다 — 패널은 좌측 핸들로 너비를 조절할
 * 수 있으므로 가로 스크롤 대신 폭에 들어가는 열까지만 보이고 나머지는 생략한다.
 */
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import AlignHorizontalLeftIcon from "@mui/icons-material/AlignHorizontalLeft";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from "recharts";

import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { mdToHtml, splitMdTables, stripMdInline, MD_TABLE_SEP_RE } from "@plannel/components/ai/aiMarkdown";
import FitTable from "@plannel/components/ai/FitTable";
import { TreeBlock } from "@plannel/components/ai/TreeTable";
import FlowBlock from "@plannel/components/ai/FlowBlock";
import ToggleButton, { ToggleButtonGroup } from "@plannel/components/ui/ToggleButton";

// 순서는 인접 슬롯의 구분 가능성으로 정해져 있다. 빨강을 주황 옆에 두면 적록색약에서 둘이 한 색으로 붙는다.
// 슬롯을 옮길 때는 인접쌍의 색각 대비를 다시 확인할 것.
export const PANEL_CHART_COLORS = ["#185FA5", "#1D9E75", "#BA7517", "#7c3aed", "#0891b2", "#E24B4A"];

/** 슬롯 색. 슬롯 수를 넘으면 순환한다 — 인접한 둘은 언제나 다른 색이다. */
export const paletteAt = (n) => PANEL_CHART_COLORS[n % PANEL_CHART_COLORS.length];

/**
 * 막대마다 색을 돌릴지 — 계열이 하나뿐인 막대 그림에서, 가로축이 서로 다른 개체라고
 * 서버가 알려 준 경우만이다.
 *
 * 계열이 하나면 색이 슬롯 0 에 고정돼 막대가 전부 같은 색이 되어 항목이 눈으로 갈라지지
 * 않는다. 계열이 둘 이상이면 색은 이미 계열이 쓰고 있으므로 건드리지 않는다. 선은 하나의
 * 경로라 조각마다 색을 줄 수 없다.
 */
export function isPerBarColored(colorByCategory, ctype, seriesCount) {
  return Boolean(colorByCategory) && ctype === "bar" && seriesCount === 1;
}

// 패널 실효 폭이 190px 안팎까지 좁아진다 — 라벨 대신 아이콘을 쓰고 여백을 최소로 줄인다.
const CHART_TOGGLE_SX = {
  "& .MuiToggleButton-root": { p: "1px 5px", border: `1px solid ${C.border}`, lineHeight: 1 },
  "& .MuiSvgIcon-root": { fontSize: 14 },
};

/** 스펙 -> 렌더. 파싱 실패·빈 데이터는 그리지 않는다(서버가 그런 스펙을 만들지 않는다). */
export function PanelChart({ raw }) {
  let spec = null;
  try { spec = JSON.parse(raw); } catch { spec = null; }
  const data = spec && Array.isArray(spec.data) ? spec.data : [];
  if (!spec || data.length === 0) return null;
  return <PanelChartView spec={spec} />;
}

/**
 * 표시 형태(선/막대, 세로/가로)는 여기서 바꾼다 — 같은 데이터를 다시 물어 재조회하지
 * 않게 하는 것이 목적이다.
 *
 * 상태를 들고 있으므로 부모(MdBubble)가 안정 key 를 줘야 한다. 인덱스 key 면 스트리밍 중
 * 세그먼트가 밀릴 때 remount 돼 사용자가 바꿔 둔 형태가 되돌아간다.
 */
function PanelChartView({ spec }) {
  const {
    // type 이 없는 스펙의 폴백은 막대다 — 서버가 첫 표시를 막대로 정한다.
    type = "bar", title, data = [], xKey, series = [],
    yLabel, caption, truncated,
    // v1 스펙(시나리오 화면 도구가 만드는 펜스)에는 없는 필드다 — 기본값이 곧 현행 동작이다.
    orientable = false, typeToggle = false, defaultLayout = "horizontal",
    colorByCategory = false,
  } = spec;

  const [ctype, setCtype] = useState(type === "bar" ? "bar" : "line");
  const [layout, setLayout] = useState(
    defaultLayout === "vertical" && type === "bar" ? "vertical" : "horizontal");
  // 선으로 바꾸면 방향 토글이 사라진다 — 가로로 눕힌 채 남겨 두면 되돌릴 수단 없이
  // 세로 선 차트가 박힌다. 종류를 바꿀 때 방향도 함께 되돌린다.
  const setType = (v) => {
    setCtype(v);
    if (v !== "bar") setLayout("horizontal");
  };

  // 폭은 자체 관측한다 — FitTable 의 측정값은 그 컴포넌트 안에 갇혀 있다.
  const wrapRef = useRef(null);
  const [w, setW] = useState(0);
  // useEffect 가 아니라 useLayoutEffect 다 — 첫 폭을 페인트 전에 넣어야 한다.
  // 안 그러면 첫 프레임이 w=0 으로 그려져 토글과 범례가 숨었다가 튀어나온다
  // (FitTable 이 같은 이유로 같은 훅을 쓴다).
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    setW(el.clientWidth || 0);
    if (typeof ResizeObserver === "undefined") return undefined;
    let raf = 0;
    const ro = new ResizeObserver((entries) => {
      const next = Math.floor(entries[entries.length - 1].contentRect.width);
      cancelAnimationFrame(raf);        // 같은 프레임에 되쓰면 ResizeObserver 루프 경고가 난다
      raf = requestAnimationFrame(() => setW((prev) => (prev === next ? prev : next)));
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);

  const autoX = xKey || Object.keys(data[0])[0];
  const autoSeries = series.length > 0
    ? series
    : Object.keys(data[0]).filter(k => k !== autoX).map(k => ({ key: k, name: k }));

  const vertical = layout === "vertical";
  const ChartComp  = ctype === "bar" ? BarChart : LineChart;
  const SeriesComp = ctype === "bar" ? Bar : Line;

  // 금액은 억 단위를 넘기 때문에 K 하나로는 "1200000K" 가 된다. M 까지 접어 라벨을 짧게 유지한다.
  // 음수도 접는다(원래는 양수만 접혀 음수 축만 유독 길어졌다). 전체 값은 툴팁이 보여 준다.
  const fmtNum = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return v;
    const a = Math.abs(n);
    if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
    if (a >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
    return n;
  };

  // 가로 막대는 항목 수만큼 세로 공간이 필요하다 — 170 고정이면 막대가 겹쳐 못 읽는다.
  // 폭은 100% 를 유지하므로 아무리 길어져도 패널 밖으로 나가지 않는다.
  const h = vertical ? Math.max(170, 44 + data.length * (18 + 6 * autoSeries.length)) : 170;
  const maxLab = data.reduce((m, d) => Math.max(m, String(d[autoX] ?? "").length), 0);
  // 수치축에 실제로 찍힐 라벨의 최대 길이. 고정 폭을 쓰면 자릿수가 큰 지표에서 왼쪽이 잘린다.
  const maxVal = data.reduce((m, d) => autoSeries.reduce((mm, s) => {
    const v = Math.abs(Number(d[s.key]));
    return Number.isFinite(v) && v > mm ? v : mm;
  }, m), 0);
  // 범주축 폭 상한 — 패널을 240px 까지 좁혀도 축이 차트를 먹지 않게 한다.
  const yw = vertical
    ? Math.max(40, Math.min(8 + maxLab * 6.5, Math.round((w || 240) * 0.38), 120))
    : Math.min(56, Math.max(30, String(fmtNum(maxVal)).length * 6.2 + 10));
  const clampTick = (v) => {
    const n = Math.max(3, Math.floor(yw / 6.5));
    const sv = String(v ?? "");
    return sv.length > n ? `${sv.slice(0, n - 1)}…` : sv;
  };
  const showLegend = autoSeries.length > 1 && w >= 180;
  const perBar = isPerBarColored(colorByCategory, ctype, autoSeries.length);
  // 방향 전환은 막대에만 준다. 막대는 길이로 크기를 말해 방향을 안 타지만, 선은 기울기로
  // 추세를 말한다 — 세로로 돌리면 증가가 위가 아니라 오른쪽이 되어 읽기가 한 번 꺾인다.
  // 애초에 돌리는 이유(긴 항목 이름·많은 항목)도 기간축을 쓰는 선 차트에는 해당하지 않는다.
  const showOrient = orientable && ctype === "bar";
  const showToggles = (typeToggle || showOrient) && w >= 200;

  const axisTick = { tick: { fontSize: FONT.MICRO }, tickLine: false };

  return (
    <Box ref={wrapRef} sx={{ width: "100%", minWidth: 0, my: 0.8 }}>
      {(title || showToggles) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.3 }}>
          <Typography sx={{
            fontSize: FONT.BODY, color: C.text3, fontWeight: 600,
            flex: 1, minWidth: 0, textAlign: showToggles ? "left" : "center",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {title || ""}
          </Typography>
          {/* 방향 토글이 먼저다 — 선 차트에서 사라지는 쪽을 안쪽에 두어야, 항상 있는
              종류 토글이 오른쪽 끝에 고정된다(자리가 왔다갔다 하면 누를 것을 못 찾는다). */}
          {showToggles && (
            <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
              {showOrient && (
                <ToggleButtonGroup size="small" exclusive value={layout}
                  onChange={(e, v) => v && setLayout(v)} sx={CHART_TOGGLE_SX}>
                  <ToggleButton value="horizontal"><AlignVerticalBottomIcon /></ToggleButton>
                  <ToggleButton value="vertical"><AlignHorizontalLeftIcon /></ToggleButton>
                </ToggleButtonGroup>
              )}
              {typeToggle && (
                <ToggleButtonGroup size="small" exclusive value={ctype}
                  onChange={(e, v) => v && setType(v)} sx={CHART_TOGGLE_SX}>
                  <ToggleButton value="bar"><BarChartIcon /></ToggleButton>
                  <ToggleButton value="line"><ShowChartIcon /></ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          )}
        </Box>
      )}
      {/* margin.left 는 0 이어야 한다. 음수 마진은 좁은 패널에서 공간을 벌어 주는 대신 축
          자체를 컨테이너 밖으로 밀어내 수치 라벨의 왼쪽을 잘라 먹는다. 폭은 yw 로 맞춘다. */}
      <ResponsiveContainer width="100%" height={h}>
        <ChartComp data={data} layout={vertical ? "vertical" : "horizontal"}
          margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          {vertical
            ? <XAxis type="number" {...axisTick} tickFormatter={fmtNum} />
            : <XAxis dataKey={autoX} {...axisTick} />}
          {vertical
            ? <YAxis type="category" dataKey={autoX} width={yw} {...axisTick} tickFormatter={clampTick} />
            : <YAxis width={yw} {...axisTick} tickFormatter={fmtNum} />}
          {/* 막대마다 색이 다를 때 툴팁 색만 계열 색(슬롯 0)을 따라가면 호버한 막대와 어긋난다 — 그때는 색을 빼고 잉크로 적는다. */}
          <RechartsTooltip
            contentStyle={{ fontSize: FONT.BODY, borderRadius: 6, border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}
            itemStyle={perBar ? { color: C.text1 } : undefined}
            formatter={v => (typeof v === "number" ? v.toLocaleString() : v)} />
          {showLegend && (
            <Legend iconSize={w < 260 ? 8 : 14} wrapperStyle={{ fontSize: FONT.CAPTION, paddingTop: 2 }} />
          )}
          {autoSeries.map((s, idx) => (
            <SeriesComp key={s.key} dataKey={s.key} name={s.name || s.key}
              stroke={paletteAt(idx)} fill={paletteAt(idx)}
              dot={false} strokeWidth={1.8} connectNulls={false}>
              {/* fill 만 주면 테두리가 계열 색으로 남아 좁은 막대에서 채움을 잠식한다. */}
              {perBar && data.map((_, i) => (
                <Cell key={i} fill={paletteAt(i)} stroke={paletteAt(i)} />
              ))}
            </SeriesComp>
          ))}
        </ChartComp>
      </ResponsiveContainer>
      {(caption || yLabel) && (
        <Typography sx={{ fontSize: FONT.MICRO, color: C.text3, textAlign: "center", mt: 0.2 }}>
          {truncated ? "⚠ " : ""}{[yLabel, caption].filter(Boolean).join(" · ")}
        </Typography>
      )}
    </Box>
  );
}

/**
 * 펜스 세그먼트의 안정 key — 내용이 같으면 항상 같은 값이라 재렌더에도 remount 되지 않는다.
 * 인덱스 key 를 쓰면 앞쪽 문단이 늘거나 줄 때 자리가 밀려 사용자가 바꿔 둔 표시 형태나
 * 접어 둔 가지가 날아간다.
 * 서버 스펙에는 id 가 이미 들어 있고(내용 해시), 없으면 원문으로 직접 접는다.
 */
function fenceKey(raw, kind) {
  const m = /"id"\s*:\s*"([^"]+)"/.exec(raw || "");
  if (m) return `${kind}-${m[1]}`;
  let hv = 5381;
  for (let i = 0; i < raw.length; i += 1) hv = ((hv * 33) ^ raw.charCodeAt(i)) >>> 0;
  return `${kind}-${hv.toString(36)}`;
}

/** 셀 값이 전부 이 모양이면 숫자 열 — 우측 정렬 + 좁은 폭 상한을 준다. */
const MD_NUM_RE = /^-?[\d,]+(\.\d+)?%?$/;

/** LLM 파이프 표 → FitTable props. 인라인 서식(**굵게** 등)은 html 로 살리고 측정은 평문으로. */
function mdTableProps(seg) {
  const cols = seg.header.length;
  // 행 길이를 헤더에 맞춘다 — 스트리밍 중 마지막 행은 짧을 수 있고(패딩), 망가진 표는 길 수
  // 있다(절단). <colgroup> 밖으로 나간 셀은 폭 없는 열을 만들어 곧 가로 넘침이다.
  const rows = seg.rows.map(r => {
    const rr = r.slice(0, cols);
    while (rr.length < cols) rr.push("");
    return rr;
  });
  const num = seg.header.map((_, c) =>
    rows.length > 0 && rows.every(r => { const v = stripMdInline(r[c]); return !v || MD_NUM_RE.test(v); }));
  return {
    header: seg.header.map((h, c) => ({ label: stripMdInline(h), num: num[c] })),
    body: rows.map(r => r.map((cell, c) => ({
      text: stripMdInline(cell),          // 측정·폴백 표시용 평문
      html: mdToHtml(cell),               // 셀은 한 줄이라 줄바꿈 치환도, 표 재귀도 없다
      bold: cell.includes("**"),          // 굵은 셀은 굵은 폰트로 잰다(과대 측정은 안전한 방향)
      num: num[c],
    }))),
  };
}

export function MdBubble({ text, streaming, bubbleSx }) {
  const segments = useMemo(() => {
    // 어떤 타입이 와도 안전하게 문자열로 변환
    let src = "";
    if (text == null) src = "";
    else if (typeof text === "string") src = text;
    else {
      try {
        src = Array.isArray(text)
          ? text.map(item => (item && typeof item === "object" ? (item.text ?? "") : String(item))).join("\n")
          : (JSON.stringify(text) ?? "");
      } catch { src = ""; }
    }
    if (!src) return [];
    // 1단계: ```chart·```tree·```flow 펜스 분리 (펜스 안 JSON 에도 '|' 가 있으므로 표 분리보다 먼저)
    const parts = [];
    const re = /```(chart|tree|flow)\s*([\s\S]*?)```/g;
    const seenKeys = {};
    let last = 0, m;
    while ((m = re.exec(src)) !== null) {
      if (m.index > last) parts.push({ type: "md", content: src.slice(last, m.index) });
      // 한 말풍선에 여러 장이 올 수 있다. 같은 스펙이 두 번이면 key 가 겹치므로 등장순을 붙인다.
      const ck = fenceKey(m[2], m[1]);
      seenKeys[ck] = (seenKeys[ck] || 0) + 1;
      parts.push({ type: m[1], raw: m[2].trim(), k: `${ck}-${seenKeys[ck]}` });
      last = re.lastIndex;
    }
    let tail = src.slice(last);
    if (streaming) {
      // 아직 안 닫힌 펜스는 숨김 (완성되면 렌더) — 먼저 열린 쪽에서 자른다
      const openIdx = ["```chart", "```tree", "```flow"]
        .map((f) => tail.indexOf(f)).filter((i) => i !== -1)
        .reduce((a, b) => Math.min(a, b), Infinity);
      if (Number.isFinite(openIdx)) tail = tail.slice(0, openIdx);
      // 꼬리의 '|' 줄 무리에 구분행이 아직 없으면 표가 되다 만 것 — raw 파이프 줄이
      // 보였다가 표로 튀지 않게 숨긴다(닫히지 않은 펜스를 숨기는 것과 같은 이유).
      // 구분행이 이미 왔다면 진짜 표다 — 숨기지 않고 행이 도착하는 대로 자라게 둔다.
      const tl = tail.split("\n");
      let sIdx = tl.length;
      while (sIdx > 0 && tl[sIdx - 1].includes("|")) sIdx -= 1;
      const run = tl.slice(sIdx);
      if (run.length > 0 && !run.some(l => MD_TABLE_SEP_RE.test(l))) tail = tl.slice(0, sIdx).join("\n");
    }
    if (tail) parts.push({ type: "md", content: tail });
    // 2단계: md 조각을 파이프 표 기준으로 한 번 더 가른다 — 표는 FitTable(폭 맞춤)이 그린다
    const out = [];
    for (const p of parts) {
      if (p.type !== "md") { out.push(p); continue; }
      for (const seg of splitMdTables(p.content)) {
        if (seg.type === "table") out.push({ type: "mdtable", ...mdTableProps(seg) });
        else if (seg.text) out.push({ type: "md", content: seg.text });
      }
    }
    return out;
  }, [text, streaming]);

  // 표·차트를 담은 말풍선은 내용 폭이 아니라 전체 폭을 쓴다 — 폭 맞춤 표는 부모의 확정폭을
  // 읽어야 하고(내용 파생 폭이면 측정이 되먹임된다), 차트도 형제 문단 폭에 끌려가지 않는다.
  const hasWide = segments.some(seg => seg.type !== "md");

  return (
    <Box sx={{
      p: "8px 10px", fontSize: FONT.SUB, lineHeight: 1.6, wordBreak: "break-word",
      ...bubbleSx,
      maxWidth: "100%", minWidth: 0, overflowWrap: "anywhere",
      alignSelf: hasWide ? "stretch" : undefined,
      "& strong": { fontWeight: 700 },
      "& em": { fontStyle: "italic" },
      "& code": { fontSize: FONT.BODY, bgcolor: "rgba(0,0,0,0.07)", px: "4px", py: "1px", borderRadius: "3px", fontFamily: "monospace", overflowWrap: "anywhere" },
      "& ul, & ol": { pl: "16px", my: "4px" },
      "& li": { mb: "2px" },
      "& hr": { border: `0.5px solid ${C.border}`, my: "6px" },
    }}>
      {segments.map((seg, i) => {
        // 펜스 세그먼트만 인덱스 key 를 쓰지 않는다 — 표시 형태와 접힌 가지를 들고 있기 때문이다.
        if (seg.type === "chart") return <PanelChart key={seg.k || i} raw={seg.raw} />;
        if (seg.type === "tree") return <TreeBlock key={seg.k || i} raw={seg.raw} />;
        if (seg.type === "flow") return <FlowBlock key={seg.k || i} raw={seg.raw} />;
        if (seg.type === "mdtable") return <FitTable key={i} header={seg.header} body={seg.body} />;
        return <Box key={i} component="div" dangerouslySetInnerHTML={{ __html: mdToHtml(seg.content) }} />;
      })}
      {streaming && (
        <Box component="span" sx={{
          display: "inline-block", width: "2px", height: "12px",
          bgcolor: C.acc, ml: "1px", verticalAlign: "text-bottom",
          animation: "cursorBlink 0.7s step-end infinite",
          "@keyframes cursorBlink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0 } },
        }} />
      )}
    </Box>
  );
}

export default MdBubble;
